import { lazy, Suspense, useMemo } from 'react'
import { Title } from '@app/components/core/typography/Title'
import { PageLayout } from '@app/pages/layout/PageLayout'
import Column from '@app/components/layout/Column'
import { useTranslations } from '@i18n/useLanguage'
import { Text } from '@app/components/core/typography/Text'
import { useQuery } from '@based/sdk/react'
import useMediaQuery from '@app/util/useMediaQuery'
import { PageBreadcrumbs } from '@app/components/blocks/pageBreadcrumbs/PageBreadcrumbs'
import { DiscoverPageLayout } from '@app/pages/layout/DiscoverPageLayout'
import useTransform from '@app/util/useTransform'
import { ViewEventModel } from 'functions/app/event/view'
import { RightColumn } from './sections/RightColumn'
import { Paragraph } from '@app/components/core/typography/Paragraph'
import { ClientOnly } from '@app/components/blocks/clientOnly/ClientOnly'
import { PageSeo } from '@app/components/core/seo/PageSeo'
import { buildEventJsonLd } from '@app/util/seo/buildEventJsonLd'
import { useRootDomain } from '@app/util/hooks/useRootDomain'
import { getEntityPageStatus } from '@app/util/getEntityPageStatus'
import { NotFoundPage } from '@app/pages/error/NotFoundPage'
import { EventPageMobile } from './EventPage.mobile'

const SingleMarkerMap = lazy(() =>
  import('@app/components/core/map/SingleMarkerMap').then((module) => ({
    default: module.SingleMarkerMap,
  })),
)

export type EventPageProps = {
  eventSlug: string
}

export const EventPage = ({ eventSlug }: EventPageProps) => {
  const isMobile = useMediaQuery()
  const translations = useTranslations().app
  const transformStyle = useTransform()

  const eventQuery = useQuery('view-event', { slug: eventSlug })
  const event: ViewEventModel | null = eventQuery.data
  const rootDomain = useRootDomain()

  const eventImages = useMemo(() => event?.images ?? [], [event?.images])

  const eventJsonLd = useMemo(() => {
    if (!event) {
      return undefined
    }

    return buildEventJsonLd({
      event,
      rootDomain,
    })
  }, [event, rootDomain])

  const pageStatus = getEntityPageStatus(eventQuery.loading, event)

  if (pageStatus === 'not-found') {
    return <NotFoundPage />
  }

  if (pageStatus === 'loading') {
    return (
      <PageLayout gap="2rem">
        <PageSeo />
        <Column
          gap="1.5rem"
          alignItems="stretch"
          style={{ marginBottom: '90vh' }}
        >
          <PageBreadcrumbs
            level2={translations.pageNames.agenda}
            level3="Loading..."
          />
        </Column>
      </PageLayout>
    )
  }

  if (isMobile && event) {
    return <EventPageMobile event={event} />
  }

  return (
    <PageLayout gap="3.5rem">
      <PageSeo
        pageTitle={event.title}
        description={event.intro}
        image={eventImages[0]}
        jsonLd={eventJsonLd}
      />

      <Column gap="2rem" alignItems="stretch" style={transformStyle}>
        <PageBreadcrumbs
          level2={translations.pageNames.agenda}
          level3={event.title}
        />

        <DiscoverPageLayout sidebar={<RightColumn event={event} />}>
          <Title.Page styledAsLevel={2}>{event.title}</Title.Page>

          <Text.Medium>{event.content}</Text.Medium>

          <div>
            <Title.H3>{translations.eventPage.address}</Title.H3>
            <Paragraph.Medium>{event.location.address}</Paragraph.Medium>
            {typeof event?.location?.geo?.lat === 'number' &&
              typeof event?.location?.geo?.long === 'number' && (
                <Suspense fallback={null}>
                  <ClientOnly fallback={null}>
                    <SingleMarkerMap
                      lat={event.location.geo.lat}
                      long={event.location.geo.long}
                      marker="pin"
                      zoom={8}
                      height="18rem"
                      style={{ borderRadius: '1.875rem' }}
                    />
                  </ClientOnly>
                </Suspense>
              )}
          </div>
        </DiscoverPageLayout>
      </Column>
    </PageLayout>
  )
}
